import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { useGetUnpackedGraphDataQuery } from "@store/services/graphsService"
import { getNodeLinkValidationState } from "@features/builder/graph/nodes/validation/pure/getNodeLinkValidationState"
import { GraphDetails } from "@features/builder/types"
import logger from "@utils/logger"
import { linkEncode } from "@components/NodeLink/linkHelpers"
import { NodeGraphLink } from "@components/NodeLink/types"
import { useNodes } from "@xyflow/react"
import { AppNode } from "@features/builder/graph/types"
import { nodesMapper } from "@features/builder/helpers/nodesMapper"

export const useNodeLinkValidation = (
  link: NodeGraphLink | undefined,
  node: AppNode | undefined,
) => {
  const hasCompleteGraphKey = !!(
    link?.org &&
    link?.div &&
    link?.prod &&
    link?.family &&
    link?.version
  )
  const graphKey = hasCompleteGraphKey
    ? linkEncode(link.org, link.div, link.prod, link.family, link.version)
    : ""
  const skip = !graphKey
  const { data: linkedGraphData, isLoading: linkedGraphIsLoading } = useGetUnpackedGraphDataQuery(
    graphKey,
    { skip },
  )
  const { data: currentGraphDocument } = useGraphDocumentData()
  const flowNodes = useNodes<AppNode>()

  // allow overriding with passed node, otherwise use the node from the flow
  // that allows us validation of node that is not yet saved
  const allNodes = flowNodes.map((flowNode) => (node?.id === flowNode.id ? node : flowNode))
  const mapper = nodesMapper(allNodes)

  let currentGraph: GraphDetails | null = null

  if (currentGraphDocument) {
    try {
      currentGraph = JSON.parse(currentGraphDocument)
      return getNodeLinkValidationState({
        currentGraph,
        linkedGraph: linkedGraphData,
        link,
        nodesMapper: mapper,
        nodeId: node?.id,
        isLinkedGraphLoading: linkedGraphIsLoading,
      })
    } catch {
      logger.error("Fail parsing current GraphDocument")
      currentGraph = null
      return getNodeLinkValidationState({
        currentGraph,
        linkedGraph: linkedGraphData,
        link,
        nodesMapper: mapper,
        nodeId: node?.id,
        isLinkedGraphLoading: linkedGraphIsLoading,
      })
    }
  }

  return getNodeLinkValidationState({
    currentGraph,
    linkedGraph: linkedGraphData,
    link,
    nodesMapper: mapper,
    nodeId: node?.id,
    isLinkedGraphLoading: linkedGraphIsLoading,
  })
}
